import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type {
  ApiResponse,
  BsbDirectoryBrowseQuery,
  BsbDirectoryBrowseResult,
  BsbDirectoryRequestError,
  TenantBsbDirectoryDetail,
} from './bsbDirectoryApiTypes';

const base = () => `${getApiBaseUrl()}/api/bsb-directory`;

/**
 * Parses the ApiResponse envelope and throws a status-bearing error.
 *
 * The status matters to the caller: 503 means the Admin-owned directory is
 * unavailable and must be shown as an error with retry, which is a different
 * state from a successful search that matched nothing.
 */
const parse = async <T>(response: Response): Promise<T> => {
  let body: ApiResponse<T> | undefined;
  try {
    body = await response.json() as ApiResponse<T>;
  } catch {
    const error: BsbDirectoryRequestError = { status: response.status, message: `Request failed: ${response.status}` };
    throw error;
  }
  if (!response.ok || !body.success || body.result == null) {
    const error: BsbDirectoryRequestError = {
      status: response.status || body.statusCode,
      message: body.message || `Request failed: ${response.status}`,
    };
    throw error;
  }
  return body.result;
};

export const browseBsbDirectory = async (
  query: BsbDirectoryBrowseQuery,
  signal?: AbortSignal,
): Promise<BsbDirectoryBrowseResult> => {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
  });
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.state) params.set('state', query.state);
  if (query.status) params.set('status', query.status);
  if (query.isElectronic !== undefined) params.set('isElectronic', String(query.isElectronic));
  return parse<BsbDirectoryBrowseResult>(await authFetch(`${base()}?${params}`, { signal }));
};

export const getBsbDirectoryDetail = async (
  bsb: string,
  signal?: AbortSignal,
): Promise<TenantBsbDirectoryDetail> =>
  parse<TenantBsbDirectoryDetail>(await authFetch(`${base()}/${encodeURIComponent(bsb)}`, { signal }));
